import { createContext, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'
import * as authService from '~/services/auth'
import type { LoginRequest, RegisterRequest } from '~/types/auth'
import type { User } from '~/types/user'

interface AuthContextType {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (credentials: LoginRequest) => Promise<void>
  loginWithGoogle: (token: string) => Promise<void>
  loginWithMicrosoft: (token: string) => Promise<void>
  register: (userData: RegisterRequest) => Promise<void>
  forgotPassword: (email: string) => Promise<boolean>
  resetPassword: (token: string, password: string) => Promise<boolean>
  logout: () => void
  isTokenExpired: (token: string) => boolean
  clearAuth: () => void
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    const storedToken = authService.getToken()
    const storedUser = authService.getUser()

    if (storedToken && storedUser && authService.isAuthenticated()) {
      setToken(storedToken)
      setUser(storedUser)
    } else {
      authService.clearAuth()
    }

    setIsLoading(false)
  }, [])
  
  // Detectar mudança de usuário e forçar reload completo da página
  const previousUserIdRef = useRef<number | undefined>(undefined)
  useEffect(() => {
    if (user?.id && previousUserIdRef.current !== undefined && previousUserIdRef.current !== user?.id) {
      // Usuário mudou - forçar reload completo para garantir que tudo seja recarregado
      // Pequeno delay para garantir que o localStorage foi atualizado
      setTimeout(() => {
        window.location.reload()
      }, 100)
      return
    }
    previousUserIdRef.current = user?.id
  }, [user?.id])

  const login = useCallback(async (credentials: LoginRequest) => {
    try {
      setIsLoading(true)
      const response = await authService.login(credentials)

      if (response.success) {
        const { access_token, user: userData } = response.data
        // Valida se o token tem formato válido
        if (
          !access_token ||
          typeof access_token !== 'string' ||
          access_token.split('.').length !== 3
        ) {
          toast.error('Erro: Token inválido recebido do servidor')
          return
        }
        authService.saveToken(access_token)
        authService.saveUser(userData)
        setToken(access_token)
        setUser(userData)
        toast.success('Login realizado com sucesso!')
        navigate('/visao-geral')
      } else {
        toast.error(response.error || 'Erro ao fazer login')
      }
    } catch (error) {
      toast.error('Erro ao fazer login')
    } finally {
      setIsLoading(false)
    }
  }, [navigate])

  const loginWithGoogle = useCallback(async (token: string) => {
    try {
      setIsLoading(true)
      const response = await authService.loginWithGoogle(token)

      if (response.success) {
        const { access_token, user: userData } = response.data
        // Valida se o token tem formato válido
        if (
          !access_token ||
          typeof access_token !== 'string' ||
          access_token.split('.').length !== 3
        ) {
          toast.error('Erro: Token inválido recebido do servidor')
          return
        }
        authService.saveToken(access_token)
        authService.saveUser(userData)
        setToken(access_token)
        setUser(userData)
        toast.success('Login com Google realizado com sucesso!')
        navigate('/visao-geral')
      } else {
        toast.error(response.error || 'Erro ao fazer login com Google')
      }
    } catch (error) {
      toast.error('Erro ao fazer login com Google')
      console.error('Google login error:', error)
    } finally {
      setIsLoading(false)
    }
  }, [navigate])

  const loginWithMicrosoft = useCallback(async (token: string) => {
    try {
      setIsLoading(true)
      const response = await authService.loginWithMicrosoft(token)

      if (response.success) {
        const { access_token, user: userData } = response.data
        // Valida se o token tem formato válido
        if (
          !access_token ||
          typeof access_token !== 'string' ||
          access_token.split('.').length !== 3
        ) {
          toast.error('Erro: Token inválido recebido do servidor')
          return
        }
        authService.saveToken(access_token)
        authService.saveUser(userData)
        setToken(access_token)
        setUser(userData)
        toast.success('Login com Microsoft realizado com sucesso!')
        navigate('/visao-geral')
      } else {
        toast.error(response.error || 'Erro ao fazer login com Microsoft')
      }
    } catch (error) {
      toast.error('Erro ao fazer login com Microsoft')
      console.error('Microsoft login error:', error)
    } finally {
      setIsLoading(false)
    }
  }, [navigate])

  const register = useCallback(async (userData: RegisterRequest) => {
    try {
      setIsLoading(true)
      const response = await authService.register(userData)

      if (response.success) {
        toast.success(
          'Cadastro realizado com sucesso! Faça login para continuar.'
        )
        navigate('/login')
      } else {
        toast.error(response.error)
      }
    } catch (error) {
      toast.error('Erro ao fazer cadastro')
    } finally {
      setIsLoading(false)
    }
  }, [navigate])

  const forgotPassword = useCallback(async (email: string): Promise<boolean> => {
    try {
      const response = await authService.forgotPassword(email)

      if (response.success) {
        toast.success(
          'Se o email estiver cadastrado, você receberá instruções para redefinir sua senha.'
        )
        return true
      } else {
        toast.error(response.error || 'Erro ao solicitar recuperação de senha')
        return false
      }
    } catch (error) {
      toast.error('Erro ao solicitar recuperação de senha')
      console.error('Forgot password error:', error)
      return false
    }
  }, [])

  const resetPassword = useCallback(async (
    token: string,
    password: string
  ): Promise<boolean> => {
    try {
      const response = await authService.resetPassword(token, password)

      if (response.success) {
        toast.success('Senha redefinida com sucesso!')
        return true
      } else {
        toast.error(response.error || 'Erro ao redefinir senha')
        return false
      }
    } catch (error) {
      toast.error('Erro ao redefinir senha. O token pode estar expirado.')
      console.error('Reset password error:', error)
      return false
    }
  }, [])

  const isTokenExpired = useCallback((token: string): boolean => {
    return authService.isTokenExpired(token)
  }, [])

  const clearAuth = useCallback(() => {
    authService.clearAuth()
    setToken(null)
    setUser(null)
  }, [])

  const logout = useCallback(() => {
    clearAuth()
    toast.success('Logout realizado com sucesso!')
    navigate('/')
  }, [clearAuth, navigate])

  const value = useMemo(
    () => ({
      user,
      token,
      isAuthenticated: !!token && !!user,
      isLoading,
      login,
      loginWithGoogle,
      loginWithMicrosoft,
      register,
      forgotPassword,
      resetPassword,
      logout,
      isTokenExpired,
      clearAuth,
    }),
    [
      user,
      token,
      isLoading,
      login,
      loginWithGoogle,
      loginWithMicrosoft,
      register,
      forgotPassword,
      resetPassword,
      logout,
      isTokenExpired,
      clearAuth,
    ],
  )

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
